// File: frontend/src/pages/customTopic/CustomTopicPage.tsx

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./CustomTopicPage.css";
import { CustomTopicModel } from "../../models/CustomTopicModel";
import { customTopicService } from "../../services/customTopicService";
import { RedirectError } from "../../services/apiClient";
import { postCustomTopicEntry, fetchEntriesByCustomTopicId } from "../../services/customTopicEntryService";
import { useCustomTopicEntries } from "../../hooks/useCustomTopicEntries";
import { useCollapsedOldEntries, CollapseEntryFetcher } from "../../hooks/useCollapsedOldEntries";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { useUrlPage, pageSuffixes, useIsEntryDeepLink, useFocusEntryId, useExitFocus } from "../../hooks/useUrlPage";
import { useFocusedEntry } from "../../hooks/useFocusedEntry";
import { Pagination } from "../../utils";
import { absoluteUrl, customTopicUrl, withCurrentQueryAndHash } from "../../utils/canonicalUrl";
import { buildTopicShareUrl } from "../../utils/shareUtils";
import { SpinnerLoading } from "../../utils";
import { EntryRow } from "../../components/entries/EntryRow";
import { CollapsedEntriesBar } from "../../components/entries/CollapsedEntriesBar";
// Sitewide pins: admin-pinned entry shown at the top of the topic page
import { PinnedEntryBlock } from "../../components/entries/PinnedEntryBlock";
import { EntryToolbar, EntrySortOption } from "../../components/entries/EntryToolbar";
import { EntryPostForm } from "../../components/entries/EntryPostForm";
import { CustomTopicOwnEntries } from "./CustomTopicOwnEntries";
import { usePreferences } from "../../context/PreferencesContext";
import { useAuth } from "../../context/AuthContext";
import { ENTRIES_PER_PAGE } from "../../constants";
import { FaHashtag, FaArrowLeft } from "react-icons/fa";
import { getRoleI18nKey, getNickaltiOwnerMark } from "../../utils/userStatusUtils";
import { USER_STATUS_ICON_MAP } from "../../utils/userStatusIconMap";
import { TOPIC_CATEGORY_ICON_MAP, TopicCategoryIconByKey } from "../../utils/topicCategoryIconMap";

interface CustomTopicPageProps {
  topicSlug: string;
  // Incremented by App.tsx on every left-pane topic-row click. Clicking the row
  // of the topic that is already open produces the same URL, so the route never
  // remounts and the entry list kept showing its stale (often empty) content
  // while the pane badge already advertised the new entry. Any change of this
  // value re-runs the entry fetch.
  reloadToken?: number;
  // Called after a new entry is successfully posted so App.tsx can
  // refresh the topic list and re-sort it (e.g. move this topic to top).
  onEntryPosted?: () => void;
  // Called when the mobile back button is tapped to return to the list pane
  onMobileBack?: () => void;
}

/**
 * Content page for a custom topic ("Gündem") at /gundem/{slug}.
 * Layout mirrors ClubPage: banner + entries (oldest first) + post form.
 * Custom topics have no sport/club metadata, so the banner is a neutral
 * dark gradient with the title and an "GÜNDEM" type pill.
 */
export const CustomTopicPage: React.FC<CustomTopicPageProps> = ({ topicSlug, reloadToken = 0, onEntryPosted, onMobileBack }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const { isAuthenticated } = useAuth();

  const [topic, setTopic]     = useState<CustomTopicModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Entry page number lives in the URL ("?sayfa=2") rather than in state, so a
  // page can be linked to and shared, survives a refresh, and gives each page
  // its own indexable address.
  const { page: urlPage, setPage: setUrlPage } = useUrlPage();

  // Sets document.title / meta description / canonical / OG tags for this
  // topic's page once it has loaded. Falls back to the site-wide defaults
  // (see useDocumentMeta) while loading or on load failure.
  useDocumentMeta({
    title: topic ? `${topic.title}${pageSuffixes(urlPage).titleSuffix} - Üç Korner Bir Penaltı` : undefined,
    description: topic
      ? `${topic.title} hakkında yazılan tanımları oku, sen de yorumunu ekle, tartış ve oyla.`
      : undefined,
    canonicalUrl: topic ? absoluteUrl(customTopicUrl(topic.slug, topic.id)) + pageSuffixes(urlPage).urlSuffix : undefined,
  });

  // Toolbar state: keyword search, filters, and sort for the entry list
  const [sort, setSort]               = useState<EntrySortOption>("oldest");
  const [filterToday, setFilterToday] = useState(false);
  const [rookieOnly, setRookieOnly]   = useState(false);
  const [keyword, setKeyword]         = useState("");

  // Anchor used by the toolbar "sayfa sonu" button to scroll down to the entry form
  const bottomRef = useRef<HTMLDivElement>(null);
  const handleScrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const showRookieEntries = preferences?.showRookieEntries !== false;
  const entriesPerPage    = preferences?.entriesPerPage ?? ENTRIES_PER_PAGE;
  const topicId           = topic?.id ?? null;

  // Sort oldest-first so the newest entry lands at the bottom, just above the
  // input form — matching the ClubPage / PersonPage / match-page layout.
  const {
    entries, loading: loadingEntries, error: entriesError,
    totalItems, currentPage, totalPages,
    isLoadingMore, loadEntries, loadMoreEntries, updateEntries,
  } = useCustomTopicEntries(topicId, showRookieEntries, sort, entriesPerPage, filterToday, rookieOnly, keyword, reloadToken, urlPage);

  // Only active in the default view: oldest sort, no filters, no keyword.
  // An address that names a page ("?sayfa=4") or an entry ("#entry-1499") is a
  // deep link, and the collapsed view cannot serve it: it renders only today's
  // entries and hides the pagination controls, so the requested page was
  // fetched and never shown and the anchored entry was never in the DOM for
  // the scroll to find. An explicit request wins over the default view.
  const isDeepLink = useIsEntryDeepLink();
  const collapseEligible =
    sort === "oldest" && !filterToday && !rookieOnly && !keyword.trim() && !isDeepLink;

  // Fetcher used by the collapse hook — always oldest sort, viewer's rookie preference
  const collapseFetcher = useCallback<CollapseEntryFetcher>(async (page, size, fToday) => {
    if (topicId === null) throw new Error("No topic selected");
    const res = await fetchEntriesByCustomTopicId(topicId, page, size, showRookieEntries, "oldest", fToday, false, "");
    return { entries: res.entries, totalItems: res.totalItems, totalPages: res.totalPages };
  }, [topicId, showRookieEntries]);

  const collapse = useCollapsedOldEntries(topicId, collapseEligible, entriesPerPage, collapseFetcher, reloadToken);

  // Permalink view: "?tanim=747" shows that one tanım framed by "{n} entry
  // daha" bars instead of dropping the reader into the middle of a page. It
  // reuses the collapse fetcher, so nothing new has to be wired per domain.
  const focusEntryId = useFocusEntryId();
  const exitFocus = useExitFocus();
  const focus = useFocusedEntry(topicId, focusEntryId, entriesPerPage, collapseFetcher);
  const handleExitFocus = useCallback(() => {
    if (focus.entry) exitFocus(focus.page, focus.entry.id);
  }, [exitFocus, focus.entry, focus.page]);

  // Entries actually rendered: collapsed view (today's + revealed older) or the normal list
  // Focused permalink view wins over both the collapsed view and the normal
  // list: the reader asked for one specific tanım.
  const displayEntries = focus.active
    ? (focus.entry ? [focus.entry] : [])
    : (collapse.active ? collapse.entries : entries);

  // Apply local mutations (vote, delete, new post) to both lists so the visible one stays correct
  const applyEntriesUpdate = useCallback((fn: (prev: typeof entries) => typeof entries) => {
    updateEntries(fn);
    collapse.updateEntries(fn);
  }, [updateEntries, collapse.updateEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Ticket 1c: the header (title, channels, share link) comes from this
    // request while the entry list comes from a second, independent one. Two
    // quick clicks in the left pane leave two topic requests in flight, and
    // without this flag the slower one wins simply by arriving last — which is
    // how the page ended up showing one topic's title above another topic's
    // entries. React runs the cleanup before re-running the effect, so the
    // superseded request is marked stale the instant a new slug is requested.
    let cancelled = false;

    setLoading(true);
    setError(null);
    setTopic(null);
    customTopicService.getTopicBySlug(topicSlug, showRookieEntries)
      .then((loaded) => {
        if (cancelled) return;
        setTopic(loaded);
      })
      .catch((err) => {
        if (cancelled) return;
        // The topic was merged — navigate to the surviving topic page
        // Covers both a merged-away topic (410) and a non-canonical
        // slug (301) — both mean "continue at redirectPath".
        if (err instanceof RedirectError) {
          // Keep "?sayfa=" and "#entry-": this fires for every legacy or
          // non-canonical slug, which is exactly what an older shared deep
          // link is. Passing the path alone dropped the page and the anchor,
          // so the reader landed on page 1 with nothing to scroll to.
          navigate(withCurrentQueryAndHash(err.redirectPath), { replace: true });
          return;
        }
        setError(t("topic_load_error", "Başlık bilgileri yüklenemedi."));
      })
      .finally(() => {
        // Ticket 1c: only the request that is still current may clear the
        // spinner — otherwise the newer topic renders with stale header state.
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [topicSlug, t, navigate, showRookieEntries]);

  // Bumped after the viewer posted or deleted an entry so the own-entry list
  // under the form is re-fetched
  const [ownEntriesVersion, setOwnEntriesVersion] = useState(0);

  const handlePostEntry = useCallback(async (content: string) => {
    if (!topicId) return;
    const newEntry = await postCustomTopicEntry(content, topicId);
    setOwnEntriesVersion(v => v + 1);
    // Append to end so the new entry appears just above the input form
    applyEntriesUpdate(prev => [...prev, newEntry]);
    loadEntries(1);
    // Notify App.tsx so it can refresh the topic list and re-sort it
    onEntryPosted?.();
  }, [topicId, applyEntriesUpdate, loadEntries, onEntryPosted]);

  const handleDeleteEntry = (entryId: number) => {
    applyEntriesUpdate(prev => prev.filter(e => e.id !== entryId));
    // A deleted own entry must disappear from the list under the form
    setOwnEntriesVersion(v => v + 1);
  };

  if (loading) return <SpinnerLoading />;
  if (error)   return <div className="topic-page__error">{error}</div>;
  if (!topic)  return <div className="topic-page__not-found">{t("topic_not_found", "Başlık bulunamadı.")}</div>;

  // Nickaltı owner lifecycle marker — null for normal topics and active owners.
  // Computed after the null guard above so `topic` is known to exist.
  const ownerMark = getNickaltiOwnerMark(topic.ownerStatus, topic.ownerClosed);
  const OwnerMarkIcon = ownerMark ? USER_STATUS_ICON_MAP[ownerMark.iconKey] : null;

  return (
    <div className="topic-page">
      {/* Mobile back button — hidden on desktop via CSS, visible on mobile */}
      {onMobileBack && (
        <button className="mobile-back-btn" onClick={onMobileBack} aria-label="Geri">
          <FaArrowLeft size={14} />
          {t("mobile_back", "Geri")}
        </button>
      )}

      {/* Neutral banner — custom topics have no brand colors or logos.
          Top row: hashtag icon (left) | title centred (flex:1) | ÖZEL pill (right). */}
      <div className="topic-page__banner">
        <div className="topic-page__banner-top">
          <div className="topic-page__banner-icon">
            <FaHashtag size={15} />
          </div>
          <h1 className="topic-page__title">
            {/* Nickaltı topics: the title is the owner's username — link it to
                their profile. Normal topics keep plain text (no owner to link to). */}
            {topic.ownerUserId && topic.ownerUsername ? (
              <Link
                to={`/yazar/${topic.ownerUsername}--${topic.ownerUserId}`}
                className="topic-page__title-link"
              >
                {topic.title}
                {topic.ownerRole && (
                  <span className="topic-page__nickalti-suffix">
                    {" "}({t(getRoleI18nKey(topic.ownerRole))})
                  </span>
                )}
                {/* Nickaltı owner marker — same rules as CustomTopicRow in the list */}
                {ownerMark && OwnerMarkIcon && (
                  <span
                    className={`topic-page__owner-mark topic-page__owner-mark--${ownerMark.modifier}`}
                    title={t(ownerMark.tooltipKey)}
                    aria-label={t(ownerMark.tooltipKey)}
                  >
                    <OwnerMarkIcon size={13} />
                  </span>
                )}
              </Link>
            ) : (
              <>
                {topic.title}
                {/* Nickaltı topics: append the owner's localized role as a suffix,
                    e.g. "apachi murat (yönetici)" — rendered only, never stored in DB,
                    so the admin-configurable 50-80 title limit is never exceeded */}
                {topic.ownerRole && (
                  <span className="topic-page__nickalti-suffix">
                    {" "}({t(getRoleI18nKey(topic.ownerRole))})
                  </span>
                )}
                {/* Nickaltı owner marker — same rules as CustomTopicRow in the list */}
                {ownerMark && OwnerMarkIcon && (
                  <span
                    className={`topic-page__owner-mark topic-page__owner-mark--${ownerMark.modifier}`}
                    title={t(ownerMark.tooltipKey)}
                    aria-label={t(ownerMark.tooltipKey)}
                  >
                    <OwnerMarkIcon size={13} />
                  </span>
                )}
              </>
            )}
          </h1>
          {/* Constant type pill — channels moved to the hashtag row below */}
          <span className="topic-page__type-label">{t("topic_type_badge", "GÜNDEM")}</span>
        </div>
        {/* All channels ("kanal") of the topic as small tags under the title,
            left to right in order (primary first) — scales cleanly up to the
            10-channel cap without crowding the top row. A channel with an
            icon_key (e.g. the reserved İstikram/Bırak system channels) shows
            its icon with the channel name as a hover tooltip instead of the
            raw "#name" text — same convention CustomTopicRow already uses for
            the primary channel's icon in the left-pane list. */}
        {topic.channels && topic.channels.length > 0 && (
          <div className="topic-page__channels">
            {topic.channels.map((ch) => (
              ch.iconKey && TOPIC_CATEGORY_ICON_MAP[ch.iconKey] ? (
                <span key={ch.id} className="topic-page__channel-tag topic-page__channel-tag--icon" title={ch.name}>
                  <TopicCategoryIconByKey iconKey={ch.iconKey} size={12} />
                </span>
              ) : (
                <span key={ch.id} className="topic-page__channel-tag">
                  #{ch.name}
                </span>
              )
            ))}
          </div>
        )}
      </div>

      {/* Toolbar: search, filters, sort, scroll-to-bottom — same behaviour as match pages.
          The author chip shows "başlığı açan": the suggesting writer, falling back to the
          admin who created the topic directly when there was no suggestion. */}
      <EntryToolbar
        sort={sort}
        onSortChange={setSort}
        filterToday={filterToday}
        onFilterTodayChange={setFilterToday}
        rookieOnly={rookieOnly}
        onRookieOnlyChange={setRookieOnly}
        keyword={keyword}
        onKeywordChange={setKeyword}
        onScrollToBottom={handleScrollToBottom}
        suggestedByUsername={topic.suggestedByUsername ?? topic.createdByUsername}
        suggestedByUserId={topic.suggestedByUserId ?? topic.createdByUserId}
        shareUrl={buildTopicShareUrl(customTopicUrl(topic.slug, topic.id))}
        shareTitle={topic.title}
      />

      {/* Entries — oldest first so the newest lands just above the input form */}
      <div className="topic-page__entries">
        {focus.loading || collapse.checking || (loadingEntries && !collapse.active && !focus.active) ? (
          <SpinnerLoading />
        ) : entriesError && !collapse.active ? (
          <p className="topic-page__empty" style={{ color: "var(--color-error)" }}>{entriesError}</p>
        ) : displayEntries.length === 0 ? (
          <p className="topic-page__empty">{t("topic_no_entries", "Henüz tanım yok.")}</p>
        ) : (
          <>
            {/* Sözlük-convention bar: older entries are collapsed, clicking reveals the previous chunk */}
            {/* Permalink view: the shared tanım is framed by a "{n} entry daha"
                bar on each side. Clicking either one leaves the focused view
                for the full page this entry sits on, with the entry still on
                screen — the bars are the way back into the topic. */}
            {focus.active && (
              <CollapsedEntriesBar
                hiddenCount={focus.hiddenAbove}
                isLoading={false}
                onClick={handleExitFocus}
              />
            )}
            {collapse.active && (
              <CollapsedEntriesBar
                hiddenCount={collapse.hiddenCount}
                isLoading={collapse.isLoadingOlder}
                onClick={collapse.loadOlderEntries}
              />
            )}
            {/* Admin-pinned entry of this topic — rendered above the regular list */}
            <PinnedEntryBlock type="CUSTOM_TOPIC" targetId={topicId} />
            {displayEntries.map(entry => (
              <EntryRow
                key={entry.id}
                entry={entry}
                onDelete={handleDeleteEntry}
                onVoteUpdate={(entryId, updates) =>
                  applyEntriesUpdate(prev => prev.map(e => e.id === entryId ? { ...e, ...updates } : e))
                }
              />
            ))}
            {focus.active && (
              <CollapsedEntriesBar
                hiddenCount={focus.hiddenBelow}
                isLoading={false}
                onClick={handleExitFocus}
              />
            )}
            {/* Bottom load-more is only for the classic view — in collapsed mode
                all of today's entries are already loaded */}
            {/* Page navigation. Reads and writes the "?sayfa=" URL parameter, so the
                reader can link to a page and the back button returns to it. */}
            {!focus.active && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                paginate={setUrlPage}
              />
            )}
            {!focus.active && !collapse.active && currentPage < totalPages && (
              <button className="topic-page__load-more" onClick={loadMoreEntries} disabled={isLoadingMore}>
                {isLoadingMore
                  ? t("load_more_loading", "yükleniyor...")
                  : t("load_more_entries", "{{count}} tanım daha", { count: totalItems - entries.length })}
              </button>
            )}
          </>
        )}
      </div>

      {/* Entry post form — always at the bottom, below all entries */}
      {/* Anchor for the toolbar "sayfa sonu" scroll target */}
      <div ref={bottomRef} />

      {isAuthenticated ? (
        <>
          <EntryPostForm
            selectedMatch={null}
            customTopicId={topicId}
            onPostEntry={handlePostEntry}
            isLocked={topic?.isLocked ?? false}
            lockReason={topic?.lockReason}
          />
          {/* Custom topics only: the viewer's earlier entries in this topic */}
          {topicId !== null && (
            <CustomTopicOwnEntries
              topicId={topicId}
              refreshKey={`${reloadToken}-${ownEntriesVersion}`}
            />
          )}
        </>
      ) : (
        <p className="login-prompt-message">{t("login_to_post_entry")}</p>
      )}
    </div>
  );
};